import {
  Button,
  ButtonProps,
  chakra,
  IconButton,
  IconButtonProps,
  List,
  ListItem,
} from '@chakra-ui/react'
import ComponentProps from '../../types/ComponentProps'
import {
  Pagination as ArkPagination,
  PaginationItemProps as ArkPaginationProps,
} from '@ark-ui/react'
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi'

export type PaginationItemProps = Omit<ArkPaginationProps, 'children'>

export interface PaginationProps extends ComponentProps {
  pageCount: number
  pageSize: number
  siblingCount?: number
  onPageChange: (details: ArkPagination.PageChangeDetails) => void
  prevButton?: IconButtonProps
  nextButton?: IconButtonProps
  Ellipsis?: ButtonProps
  pageButton?: ButtonProps
  isRounded?: Boolean
  page?: number
}

/**
 * Represents the properties for the Pagination component.
 *
 * @param {number} pageCount - The total number of pages available for pagination.
 * @param {number} pageSize - The number of items per page, which determines how many items are displayed on each page.
 * @param {number} siblingCount - The number of sibling pages to display on either side of the current page; this can help provide context for navigation.
 * @param {void} onPageChange - Callback function that is called when the page is changed. It receives an object containing details about the page change.
 * @param {ButtonProps} prevButton - Props to customize the appearance and behavior of the previous button in the pagination controls.
 * @param {ButtonProps} nextButton - Props to customize the appearance and behavior of the next button in the pagination controls.
 * @param {ButtonProps} Ellipsis - Props to customize the appearance of the ellipsis button that indicates skipped pages in the pagination.
 * @param {ButtonProps} pageButton - Props to customize the appearance and behavior of the individual page number buttons.
 * @param {boolean} isRounded - A boolean value that determines if the buttons in the pagination should have rounded corners. If true, buttons will have a full border radius; if false, buttons will have a medium border radius.
 *
 */
export default chakra(Pagination)

function Pagination({
  theme = 'brand',
  pageCount,
  pageSize,
  siblingCount,
  onPageChange,
  children,
  prevButton,
  nextButton,
  pageButton,
  Ellipsis,
  isRounded,
  page,
  ...props
}: PaginationProps) {
  const borderRadius = isRounded ? 'full' : 'md'
  return (
    <ArkPagination.Root
      {...props}
      count={pageCount}
      page={page}
      pageSize={pageSize}
      siblingCount={siblingCount}
      onPageChange={(details) => {
        onPageChange(details)
      }}
    >
      <List display='flex' justifyContent='space-between' gap={1}>
        <ListItem>
          <ArkPagination.PrevTrigger asChild>
            <IconButton
              borderRadius={borderRadius}
              icon={<FiArrowLeft />}
              aria-label='Previous Page'
              {...prevButton}
            />
          </ArkPagination.PrevTrigger>
        </ListItem>

        <List display={'flex'} gap={1}>
          <ArkPagination.Context>
            {(pagination) =>
              pagination.pages.map((page, index) =>
                page.type === 'page' ? (
                  <ListItem key={index}>
                    <ArkPagination.Item
                      key={'PaginationItem-' + index}
                      asChild
                      {...page}
                    >
                      <Button borderRadius={borderRadius} {...pageButton}>
                        {page.value}
                      </Button>
                    </ArkPagination.Item>
                  </ListItem>
                ) : (
                  <ListItem key={index} alignItems='center' display='flex'>
                    <ArkPagination.Ellipsis
                      key={'PaginationEllipsis-' + index}
                      index={index}
                    >
                      <Button
                        {...Ellipsis}
                        color="gray.500"
                        borderRadius={borderRadius}
                        pointerEvents='none'
                      >
                        &#8230;
                      </Button>
                    </ArkPagination.Ellipsis>
                  </ListItem>
                ),
              )
            }
          </ArkPagination.Context>
        </List>

        <ListItem>
          <ArkPagination.NextTrigger asChild>
            <IconButton
              borderRadius={borderRadius}
              icon={<FiArrowRight />}
              aria-label='Previous Page'
              {...nextButton}
            />
          </ArkPagination.NextTrigger>
        </ListItem>
      </List>
    </ArkPagination.Root>
  )
}
